<template>
  <div id="app">
    <router-view />
    <theme-picker />
  </div>
</template>

<script>
import ThemePicker from "@/components/ThemePicker"
import { resolveLegacyPage } from "@/config/legacy-page-mapping"

export default {
  name: "App",
  components: { ThemePicker },
  mounted() {
    // 监听旧版iframe页面通过postMessage发起的路由跳转请求
    window.addEventListener('message', (event) => {
      // 安全检查：只处理来自已知开发服务器端口的消息（8081是Thymeleaf旧页面）
      console.log('App.vue测试')
      var allowedOrigins = [window.location.origin, 'http://localhost:8081']
      if (allowedOrigins.indexOf(event.origin) === -1) return
      if (!event.data) return
      const msgType = event.data.type

      // 打开iframe子页签
      if (msgType === 'vue-navigate') {
        const { iframeUrl, title, closeCurrent, uniqueKey } = event.data
        if (!iframeUrl) return

        // 通过 legacy 映射解析 iframeUrl，与 permission.js 路由生成保持一致
        // 例如 "fixedBoardPlan/init?type=ALL&mid=240" → "airway/fixedBoardPlan/fixedBoard.html?type=ALL&mid=240"
        const qIdx = iframeUrl.indexOf('?')
        const pathPart = qIdx !== -1 ? iframeUrl.substring(0, qIdx) : iframeUrl
        const queryPart = qIdx !== -1 ? iframeUrl.substring(qIdx) : ''
        const legacyResult = resolveLegacyPage(pathPart, queryPart)
        const normalizedIframeUrl = legacyResult.isLegacy ? (legacyResult.iframeUrl + queryPart) : iframeUrl
        const normalizedBasePath = legacyResult.isLegacy ? legacyResult.iframeUrl : (qIdx !== -1 ? iframeUrl.substring(0, qIdx) : iframeUrl)

        // 先检查是否已有相同标题/唯一键的页签，如果有则直接激活而非新建重复页签
        const visitedViews = this.$store.state.tagsView.visitedViews
        // 如果调用方提供了 uniqueKey，优先按 uniqueKey 精确匹配
        // uniqueKey 匹配不上说明意图是打开独立新页签，不再做其他兜底匹配
        let existingView = null
        // 1. 优先按 uniqueKey 精确匹配（支持同标题、不同参数的多个独立页签）
        if (uniqueKey) {
          existingView = visitedViews.find(v => {
            if (!v.meta) return false
            return v.meta.uniqueKey === uniqueKey || v.uniqueKey === uniqueKey
          })
        }
        // 2. uniqueKey 未匹配到时，通过 iframeUrl / 基础路径 / 标题兜底匹配
        //    closeCurrent 场景（如 updateAddJZ 保存后回列表页）也需要兜底，
        //    因为已有页签的 uniqueKey 可能与 postMessage 传来的不一致
        //    带 uniqueKey 但没命中时不再按基础路径复用，避免“带单号打开”命中默认订舱页后再被旧地址覆盖
        if (!existingView && !uniqueKey) {
          existingView = visitedViews.find(v => {
            if (!v.meta) return false
            // 如果 iframeUrl（解析后）相同，也视为同一页签
            if (v.meta.iframeUrl && v.meta.iframeUrl === normalizedIframeUrl) return true
            // 如果页面基础路径相同，也视为同一页签（处理查询参数不一致的情况）
            if (v.meta.iframeUrl) {
              const vBasePath = v.meta.iframeUrl.split('?')[0]
              if (vBasePath === normalizedBasePath) return true
            }
            // 兜底：当前请求要求关闭自身（closeCurrent）且标题相同、是 iframe 页签，
            // 视为要回到原页签，直接复用
            if (closeCurrent && v.meta.title === title && v.meta.isIframePage) return true
            return false
          })
        }
        if (!existingView && uniqueKey && closeCurrent) {
          existingView = visitedViews.find(v => {
            if (!v.meta) return false
            return closeCurrent && v.meta.title === title && v.meta.isIframePage
          })
        }
        if (existingView) {
          // 更新已有页签的 meta 数据（如 iframeUrl、link、title），确保页面内容正确刷新
          const iframeFullUrl = `${process.env.NODE_ENV === 'development' ? 'http://localhost:8081' : ''}/modules/${normalizedIframeUrl}`
          const finalTitle = title || existingView.meta.title || '页面'
          // _refreshKey 递增，强制 IframeToggle 销毁重建 iframe，即使 link 相同也会重新加载
          const newRefreshKey = ((existingView.meta && existingView.meta._refreshKey) || 0) + 1
          // 就地改路由 meta，避免 $router.push 后 TagsView 用旧 $route.meta 把单号地址覆盖回去
          if (existingView.meta) {
            existingView.meta.title = finalTitle
            existingView.meta.link = iframeFullUrl
            existingView.meta.iframeUrl = normalizedIframeUrl
            existingView.meta._refreshKey = newRefreshKey
          }
          const updatedMeta = {
            ...(existingView.meta || {}),
            title: finalTitle,
            link: iframeFullUrl,
            iframeUrl: normalizedIframeUrl,
            _refreshKey: newRefreshKey
          }
          this.$store.dispatch('tagsView/updateVisitedView', {
            path: existingView.path,
            title: finalTitle,
            meta: updatedMeta
          })
          this.$store.dispatch('tagsView/updateIframeView', {
            path: existingView.path,
            title: finalTitle,
            meta: updatedMeta,
            query: {}
          })
          // 如需关闭当前页签，在导航前关闭
          if (closeCurrent) {
            this.$store.dispatch('tagsView/delView', this.$route)
          }
          // 已在当前页签时不要再 push 旧 query，否则 iframe 会按打开页的默认地址再加载一次
          if (this.$route.path !== existingView.path) {
            this.$router.push({ path: existingView.path })
          }
          return
        }

        // 无已有页签，新建
        const currentRoute = closeCurrent ? this.$route : null
        // 生成唯一路由路径，包裹Layout保证在右侧页签区域显示
        const timestamp = Date.now()
        const routePath = '/iframe-legacy/' + timestamp
        // 动态添加路由并跳转（包裹Layout以显示侧边栏+页签+主内容区）
        // meta.link 设置为完整 iframe URL，走 IframeToggle（v-show切换），保持页面状态
        // 使用解析后的 normalizedIframeUrl 以保证与侧边栏菜单的 meta.link 一致
        const iframeFullUrl = `${process.env.NODE_ENV === 'development' ? 'http://localhost:8081' : ''}/modules/${normalizedIframeUrl}`
        this.$router.addRoutes([{
          path: routePath,
          component: () => import('@/layout'),
          redirect: 'noRedirect',
          hidden: true,
          children: [{
            path: 'index',
            name: 'IframePage',
            component: () => import('@/views/iframe/index'),
            meta: { title: title || '页面', isIframePage: true, link: iframeFullUrl, iframeUrl: normalizedIframeUrl, uniqueKey: uniqueKey || title || '页面' }
          }]
        }])
        // 持久化到 sessionStorage，刷新后 GenerateRoutes 恢复该路由，避免 404
        try {
          const stored = JSON.parse(sessionStorage.getItem('_legacy_routes') || '[]')
          stored.push({
            timestamp,
            normalizedIframeUrl,
            title: title || '页面',
            link: iframeFullUrl,
            uniqueKey: uniqueKey || title || '页面'
          })
          sessionStorage.setItem('_legacy_routes', JSON.stringify(stored))
        } catch(e) { /* ignore */ }
        // 导航后关闭原来的页签
        if (currentRoute) {
          this.$router.push({ path: routePath + '/index' })
          this.$nextTick(() => {
            this.$store.dispatch('tagsView/delView', currentRoute)
          })
        } else {
          this.$router.push({ path: routePath + '/index' })
        }
        return
      }

      // 关闭指定页签（按 uniqueKey 匹配）
      if (msgType === 'vue-close-tab') {
        const { uniqueKey } = event.data
        if (uniqueKey) {
          const targetView = this.$store.state.tagsView.visitedViews.find(v =>
            (v.meta && v.meta.uniqueKey === uniqueKey) || v.uniqueKey === uniqueKey
          )
          if (targetView) {
            this.$store.dispatch('tagsView/delView', targetView).then(() => {
              if (this.$route.path === targetView.path) {
                this.$router.go(-1)
              }
            })
          }
        }
        return
      }

      // 关闭当前页签
      if (msgType === 'vue-close-current-tab') {
        this.$store.dispatch('tagsView/delView', this.$route).then(() => {
          this.$router.go(-1)
        })
        return
      }
    })
  }
}
</script>
<style scoped>
#app .theme-picker {
  display: none;
}
</style>
